import React from 'react';
import { Save } from 'lucide-react';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';

const EmailTemplateCard = ({ title, templateKey, color, bgColor, emailTemplates, updateEmailTemplate, sendTestEmail, sendingTest, testEmail }) => (
  <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '1.5rem', background: bgColor || 'white' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
      <h4 style={{ fontSize: '1rem', fontWeight: '600', color, margin: 0 }}>{title}</h4>
      <button 
        onClick={() => sendTestEmail(templateKey)}
        disabled={sendingTest === templateKey || !testEmail}
        className="btn-secondary"
        style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
      >
        {sendingTest === templateKey ? 'Sende...' : 'Test senden'}
      </button>
    </div>
    <div className="form-group">
      <label className="form-label">Betreff</label>
      <input
        type="text"
        value={emailTemplates[templateKey]?.subject || ''}
        onChange={(e) => updateEmailTemplate(templateKey, 'subject', e.target.value)}
        className="form-input"
        placeholder="E-Mail Betreff..."
      />
    </div>
    <div className="form-group">
      <label className="form-label">Nachricht</label>
      <TinyMCEEditor
        apiKey={process.env.REACT_APP_TINYMCE_API_KEY}
        value={emailTemplates[templateKey]?.text_body || ''}
        onEditorChange={(content) => updateEmailTemplate(templateKey, 'text_body', content)}
        init={{
          height: templateKey === 'internal_lead_notification' || templateKey === 'partner_lead_forward' ? 400 : 300,
          menubar: false,
          plugins: 'bold italic underline link lists',
          toolbar: 'bold italic underline | link | bullist numlist',
          branding: false,
          content_style: 'body { font-family: Arial, sans-serif; font-size: 14px; }'
        }}
      />
    </div>
  </div>
);

const EmailTab = ({
  config, updateConfigValue,
  emailTemplates, updateEmailTemplate,
  testEmail, setTestEmail,
  saveEmailTemplates, savingTemplates,
  sendTestEmail, sendingTest
}) => {
  const templateDefs = [
    { key: 'lead_confirmation', title: 'Lead-Bestätigung (Regulärer Lead)', color: '#1B5E20' },
    { key: 'internal_lead_notification', title: 'Interne Lead-Benachrichtigung (An Sie)', color: '#F57C00', bg: '#FFF9E6' },
    { key: 'waitlist_confirmation', title: 'Wartelisten-Bestätigung', color: '#F57C00' },
    { key: 'convert_notification', title: 'Warteliste → Regulär Benachrichtigung', color: '#9C27B0' },
    { key: 'partner_lead_forward', title: 'Lead-Weiterleitung an Partner', color: '#2E7D32', bg: '#E8F5E9' },
    { key: 'partner_followup', title: 'Partner-Follow-up', color: '#2196F3' },
  ];

  return (
    <div style={{ display: 'grid', gap: '3rem' }}>
      {/* SMTP Configuration */}
      <div>
        <h3 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.5rem' }}>📧 SMTP-Konfiguration</h3>
        <div style={{ display: 'grid', gap: '1rem', maxWidth: '600px' }}>
          <div className="form-group">
            <label className="form-label">Absender E-Mail</label>
            <input
              type="email"
              value={config.email?.from || ''}
              onChange={(e) => updateConfigValue('email.from', e.target.value)}
              className="form-input"
              data-testid="email-from-input"
            />
          </div>
        </div>
      </div>

      {/* Email Templates */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ fontSize: '1.125rem', fontWeight: '600' }}>📝 E-Mail Templates</h3>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'end' }}>
            <div style={{ flex: '1' }}>
              <label style={{ fontSize: '0.875rem', color: '#666', display: 'block', marginBottom: '0.25rem' }}>📧 Test-E-Mail Adresse</label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="ihre.email@example.com"
                className="form-input"
                style={{ fontSize: '0.875rem' }}
              />
            </div>
            <button 
              onClick={saveEmailTemplates}
              disabled={savingTemplates}
              className="btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <Save size={16} /> {savingTemplates ? 'Speichere...' : 'Templates speichern'}
            </button>
          </div>
        </div>

        {/* Variable Reference */}
        <details style={{ background: '#FFF8E1', padding: '1rem', borderRadius: '8px', marginBottom: '2rem', fontSize: '0.875rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>📋 Alle verfügbaren Variablen für E-Mail-Templates</summary>
          <div style={{ marginTop: '1rem' }}>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Verwenden Sie Variablen in geschweiften Klammern: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '4px' }}>{"{variable_name}"}</code>
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
              <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <h5 style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#1B5E20' }}>👤 Kundendaten</h5>
                <div style={{ fontSize: '0.8rem', lineHeight: '1.8' }}>
                  <code>{"{name}"}</code>, <code>{"{email}"}</code>, <code>{"{phone}"}</code>, <code>{"{address}"}</code>, <code>{"{plz}"}</code>, <code>{"{country}"}</code>, <code>{"{currency}"}</code>
                </div>
              </div>
              <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <h5 style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#E65100' }}>💰 Ergebnisvariablen</h5>
                <div style={{ fontSize: '0.8rem', lineHeight: '1.8' }}>
                  <code>{"{invest_beginn_separatus}"}</code>, <code>{"{vollkosten_separatus}"}</code>, <code>{"{ersparnis_vollkosten}"}</code>, etc.
                </div>
              </div>
            </div>
          </div>
        </details>

        <div style={{ display: 'grid', gap: '2rem' }}>
          {templateDefs.map(def => (
            <EmailTemplateCard
              key={def.key}
              title={def.title}
              templateKey={def.key}
              color={def.color}
              bgColor={def.bg}
              emailTemplates={emailTemplates}
              updateEmailTemplate={updateEmailTemplate}
              sendTestEmail={sendTestEmail}
              sendingTest={sendingTest}
              testEmail={testEmail}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmailTab;
